import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SwipeableViews from 'react-swipeable-views'

import {
  Box,
  CircularProgress,
  MobileStepper,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import authHook from '@hooks/useAuth'
import errorHandlerHook from '@hooks/useErrorHandler'

import PaymentService from '@services/payment'

import { PromotionCodeAlert } from '@components/PromotionCodeAlert'

import CurrencyUtils from '@utils/currency'
import {
  ISelectableInterval,
  getBiggestThreePaymentFrequenciesByPeriod,
} from '@utils/plan-detail'
import { FEATURES_LIST } from '@utils/plans'
import {
  CustomPlanName,
  ICoupon,
  type IPlan,
  IPromotionCode,
  PaymentPlanStatus,
  StandardPlanName,
} from '@utils/types'

import {
  availablePlansWrapperSx,
  backButtonSx,
  loadingWrapperSx,
  mobileStepperSx,
  nextButtonSx,
  paperSx,
  promotionCodeAlertWrapperSx,
  wrapperSx,
} from './styles'

import { CouponAlert } from './components/CouponAlert'
import { PlanItem } from './components/PlanItem'
import { PLAN_SECTION_MAX_WIDTH_IN_PX } from './components/PlanItem/styles'

export type PlanName = StandardPlanName | CustomPlanName

export interface IFeature {
  name: string
  [StandardPlanName.BETA]: boolean | string
  [StandardPlanName.STARTER]: boolean | string
  [StandardPlanName.PLUS]: boolean | string
  [StandardPlanName.MAX]: boolean | string
  [CustomPlanName.ENTERPRISE]: boolean | string
}

interface IDefinedPlan {
  alias: string
  name: PlanName
  paymentPeriods?: ISelectableInterval[]
  isHighlighted?: boolean
  isAvailable?: boolean
  isCustomPlan: boolean
  onClick?: () => void
}

interface IPlansProps {
  promotionCode?: IPromotionCode
  onPlanClick: ({
    plan,
    coupon,
  }: {
    plan: IPlan
    coupon: ICoupon | null
  }) => void
}

const StandardPlans = ({ promotionCode, onPlanClick }: IPlansProps) => {
  const { t } = useTranslation()
  const { isVendor } = authHook.useAuth()
  const { handleError } = errorHandlerHook.useErrorHandler()
  const theme = useTheme()

  const [isLoadingStandardPlans, setIsLoadingStandardPlans] = useState(true)
  const [standardPlans, setStandardPlans] = useState<IPlan[]>()
  const [planItems, setPlanItems] = useState<IDefinedPlan[]>([])
  const [activeStep, setActiveStep] = useState(0)
  const [features, setFeatures] = useState<IFeature[]>(FEATURES_LIST)
  const [couponAvailable, setCouponAvailable] = useState<ICoupon | undefined>()

  const handleOnPlanClick = useCallback(
    ({ plan }: { plan: IPlan }) => {
      if (plan?.status !== PaymentPlanStatus.AVAILABLE) {
        return
      }

      onPlanClick({ plan, coupon: null })
    },
    [onPlanClick]
  )

  const fetchMyAvailableCoupon = async () => {
    try {
      const response = await PaymentService.getMyAvailableCoupon()

      setCouponAvailable(response)
    } catch (err) {
      handleError({ exception: err })
    }
  }

  useEffect(() => {
    if (isVendor()) {
      fetchMyAvailableCoupon()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor])

  const fetchPaymentPlans = useCallback(async () => {
    try {
      setIsLoadingStandardPlans(true)

      const response = await PaymentService.findStandardPlansAsVisitor()

      setFeatures((prevFeatures) => {
        const updatedFeatures = prevFeatures.map((v) => {
          const solutionLandingPages = v.name === 'Solution Landing Pages'
          const searchAnalyticsFeature = v.name === 'Search Analytics'
          const marketTrendsFeature = v.name === 'Market Trends'
          const solutionTrendsFeature = v.name === 'Solution Trends'
          const supportHoursIncluded = v.name === 'Support Hours Included'
          const hourlyRateForAdditionalSupportHours =
            v.name === 'Additional Support Hours'

          for (const plan of response) {
            const planRef = plan.name as PlanName
            const isPlanAvailable = plan.status === PaymentPlanStatus.AVAILABLE

            const { maxSolutions, supportHoursRate, includedSupportHours } =
              plan?.settings ?? {}

            if (isPlanAvailable) {
              if (solutionLandingPages) {
                v[planRef] = `(${t('Up to')} ${maxSolutions ?? 0})`
              }

              if (supportHoursIncluded) {
                if (includedSupportHours) {
                  v[planRef] = `- ${includedSupportHours}hr/month`
                } else {
                  v[planRef] = false
                }
              }

              if (hourlyRateForAdditionalSupportHours) {
                if (supportHoursRate) {
                  v[planRef] = `- ${CurrencyUtils.toCurrencyFormat(
                    supportHoursRate ?? 0
                  )}/hour`
                } else {
                  v[planRef] = false
                }
              }

              if (
                searchAnalyticsFeature ||
                marketTrendsFeature ||
                solutionTrendsFeature
              ) {
                v[planRef] = !!plan?.features?.marketTrends
              }
            }
          }

          return v
        })

        return updatedFeatures
      })

      setPlanItems([
        ...response
          .filter((v) => v.status !== PaymentPlanStatus.HIDDEN)
          .map((plan) => {
            return {
              alias: plan.alias,
              name: plan.name as PlanName,
              paymentPeriods: getBiggestThreePaymentFrequenciesByPeriod({
                plan,
              }),
              isAvailable: plan.status === PaymentPlanStatus.AVAILABLE,
              isHighlighted: plan.highlighted,
              isCustomPlan: !!plan.custom,
              onClick: () => handleOnPlanClick({ plan }),
            }
          }),
        {
          alias: t('Enterprise'),
          name: CustomPlanName.ENTERPRISE,
          isCustomPlan: true,
        },
      ])

      setStandardPlans(response)
    } catch (err) {
      handleError({ exception: err })
    }

    setIsLoadingStandardPlans(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError, t])

  useEffect(() => {
    fetchPaymentPlans()
  }, [fetchPaymentPlans])

  const handleStepChange = (step: number) => {
    if (step < 0 || step >= planItems.length) {
      return
    }

    setActiveStep(step)
  }

  const handleOnClaimOfferButtonClick = ({
    plan,
    coupon,
  }: {
    plan: IPlan
    coupon: ICoupon
  }) => {
    onPlanClick({ plan, coupon })
  }

  const planSetWidth = Math.floor(
    planItems.length * PLAN_SECTION_MAX_WIDTH_IN_PX.DESKTOP
  )
  const mobileView = useMediaQuery(theme.breakpoints.down(planSetWidth))

  if (isLoadingStandardPlans) {
    return (
      <Stack sx={loadingWrapperSx}>
        <CircularProgress color="secondary" />
      </Stack>
    )
  }

  if (mobileView) {
    return (
      <>
        {!!couponAvailable && (
          <CouponAlert
            coupon={couponAvailable}
            standardPlans={standardPlans}
            onClaimOffer={handleOnClaimOfferButtonClick}
          />
        )}
        {!!promotionCode && (
          <Stack sx={promotionCodeAlertWrapperSx}>
            <PromotionCodeAlert
              variant="checkout"
              promotionCode={promotionCode}
            />
          </Stack>
        )}
        <Box sx={wrapperSx}>
          <SwipeableViews
            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
            index={activeStep}
            onChangeIndex={handleStepChange}
            enableMouseEvents
          >
            {planItems.map((plan) => (
              <Box
                key={plan.name}
                sx={availablePlansWrapperSx}
                data-testid="plan-swipeable-view"
              >
                <ChevronLeftIcon
                  onClick={() => handleStepChange(activeStep - 1)}
                  sx={backButtonSx}
                />
                <ChevronRightIcon
                  onClick={() => handleStepChange(activeStep + 1)}
                  sx={nextButtonSx}
                />
                <PlanItem
                  name={plan.name}
                  alias={plan.alias}
                  features={features}
                  paymentPeriods={plan.paymentPeriods}
                  isLoading={isLoadingStandardPlans}
                  isAvailable={plan.isAvailable}
                  isHighlighted={plan.isHighlighted || mobileView}
                  isCustomPlan={plan.isCustomPlan}
                  planSetWidth={planSetWidth}
                  mobileView={mobileView}
                  onClick={plan.onClick}
                />
                <MobileStepper
                  steps={planItems.length}
                  position="static"
                  activeStep={activeStep}
                  nextButton={undefined}
                  backButton={undefined}
                  sx={mobileStepperSx}
                />
              </Box>
            ))}
          </SwipeableViews>
        </Box>
      </>
    )
  }

  return (
    <>
      {!!couponAvailable && (
        <CouponAlert
          coupon={couponAvailable}
          standardPlans={standardPlans}
          onClaimOffer={handleOnClaimOfferButtonClick}
        />
      )}
      {!!promotionCode && (
        <Stack sx={promotionCodeAlertWrapperSx}>
          <PromotionCodeAlert
            variant="checkout"
            promotionCode={promotionCode}
          />
        </Stack>
      )}
      <Paper sx={paperSx} elevation={16}>
        {planItems.map((plan) => (
          <PlanItem
            key={plan.name}
            name={plan.name}
            alias={plan.alias}
            features={features}
            paymentPeriods={plan.paymentPeriods}
            isLoading={isLoadingStandardPlans}
            isAvailable={plan.isAvailable}
            isHighlighted={plan.isHighlighted}
            isCustomPlan={plan.isCustomPlan}
            hasPromotionCode={!!promotionCode}
            planSetWidth={planSetWidth}
            mobileView={mobileView}
            onClick={plan.onClick}
          />
        ))}
      </Paper>
    </>
  )
}

export { StandardPlans }
